import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ConfirmationNumber as TokenIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../layouts/DashboardLayout';
import { getApiBaseUrl, getAuthHeader } from '../../services/httpClient';

const API_BASE_URL = getApiBaseUrl();

const emptySettings = {
  postpaid: false,
  packId: 'hotel',
  theme: 'desk',
  serveAtTable: false,
  showTables: false,
  splitBoard: false,
  hideNumbers: false,
  askAfterBill: false,
  showItemNames: true,
  hideSameCategoryAds: true,
  showLoksyDigitalPamphlets: true,
  repeatOpFreeDays: 0,
};

const PAY_OPTIONS = [
  { value: false, label: 'Prepaid — pay first' },
  { value: true, label: 'Postpaid — pay when leaving' },
];

function payChip(postpaid, { mixed = false } = {}) {
  if (mixed) {
    return <Chip size="small" color="default" label="Mixed" />;
  }
  return postpaid ? (
    <Chip size="small" color="warning" label="Postpaid" />
  ) : (
    <Chip size="small" color="success" variant="outlined" label="Prepaid" />
  );
}

function groupPaySummary(group) {
  const cats = group.categories || [];
  if (!cats.length) {
    return { postpaid: !!group.resolved?.postpaid, mixed: false, pack: group.resolved?.packId || 'hotel' };
  }
  const post = cats.filter((c) => c.resolved?.postpaid).length;
  const packs = [...new Set(cats.map((c) => c.resolved?.packId).filter(Boolean))];
  return {
    postpaid: post === cats.length,
    mixed: post > 0 && post < cats.length,
    pack: packs.length === 1 ? packs[0] : packs.length ? 'mixed' : group.resolved?.packId || 'hotel',
    prepaidCount: cats.length - post,
    postpaidCount: post,
  };
}

function sourceChip(source) {
  if (source === 'saved') return <Chip size="small" label="Saved" color="primary" />;
  if (source === 'inherited') return <Chip size="small" label="From group" variant="outlined" />;
  return <Chip size="small" label="Code default" variant="outlined" />;
}

export default function MitraTokenBoardSettings() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [payFilter, setPayFilter] = useState('all');
  const [snack, setSnack] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/token-board-category-defaults`, {
        headers: getAuthHeader(),
      });
      if (res.data?.success) setData(res.data.data);
      else setError(res.data?.message || 'Could not load defaults');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load Token Board defaults');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const packs = data?.packs || [];
  const groups = data?.groups || [];
  const platform = data?.platform;

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups
      .map((g) => {
        const cats = (g.categories || []).filter((c) => {
          const hay = `${c.label} ${c.category_key} ${c.sub_category_id} ${g.label}`.toLowerCase();
          if (q && !hay.includes(q)) return false;
          if (payFilter === 'prepaid' && c.resolved?.postpaid) return false;
          if (payFilter === 'postpaid' && !c.resolved?.postpaid) return false;
          return true;
        });
        const groupHit = !q || String(g.label).toLowerCase().includes(q);
        if (!cats.length && !groupHit) return null;
        if (q && !cats.length && groupHit) return { ...g, categories: g.categories || [] };
        return { ...g, categories: cats };
      })
      .filter(Boolean);
  }, [groups, search, payFilter]);

  const openEditor = (kind, row, group) => {
    const resolved = { ...emptySettings, ...(row.resolved || {}) };
    setEditor({
      kind,
      category_key: kind === 'platform' ? '__platform__' : kind === 'main' ? row.main_category_id : row.category_key,
      scope: kind === 'platform' ? 'platform' : kind === 'main' ? 'main' : 'sub',
      main_category_id: kind === 'main' ? row.main_category_id : row.main_category_id,
      sub_category_id: row.sub_category_id || '',
      label: row.label,
      groupLabel: group?.label || '',
      settings: resolved,
    });
  };

  const saveEditor = async () => {
    if (!editor) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/admin/token-board-category-defaults`,
        {
          category_key: editor.category_key,
          scope: editor.scope,
          main_category_id: editor.main_category_id,
          sub_category_id: editor.sub_category_id,
          settings: editor.settings,
        },
        { headers: getAuthHeader() },
      );
      if (res.data?.success) {
        setSnack({ severity: 'success', message: `Saved defaults for ${editor.label}` });
        setEditor(null);
        await load();
      } else {
        setSnack({ severity: 'error', message: res.data?.message || 'Save failed' });
      }
    } catch (err) {
      setSnack({
        severity: 'error',
        message: err.response?.data?.message || 'Save failed',
      });
    } finally {
      setSaving(false);
    }
  };

  const applyGroupPay = async (group, postpaid) => {
    const keys = [
      group.main_category_id,
      ...(group.categories || []).map((c) => c.category_key),
    ].filter(Boolean);
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/admin/token-board-category-defaults/bulk`,
        { keys, settings: { postpaid } },
        { headers: getAuthHeader() },
      );
      setSnack({
        severity: 'success',
        message: `${group.label} set to ${postpaid ? 'postpaid' : 'prepaid'}`,
      });
      await load();
    } catch (err) {
      setSnack({
        severity: 'error',
        message: err.response?.data?.message || 'Bulk update failed',
      });
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, value) => {
    setEditor((prev) =>
      prev ? { ...prev, settings: { ...prev.settings, [key]: value } } : prev,
    );
  };

  return (
    <DashboardLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #c2410c 0%, #ea580c 100%)',
            color: '#fff',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TokenIcon sx={{ fontSize: 40 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={800}>
                Mitra Token Board Settings
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.95 }}>
                Change category defaults without coding. Restaurant prepaid → postpaid lives here.
                Shops that already customized their board keep their own settings.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="inherit"
              sx={{ color: '#c2410c', fontWeight: 700 }}
              onClick={() => navigate('/business-category-index')}
            >
              Category index
            </Button>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          Categories stay in sync with{' '}
          <strong>Shops &amp; Services Index</strong>. Search a shop type (e.g.{' '}
          <strong>restaurant</strong>), open the group, then Edit payment mode. Uncustomized
          Mitra shops pick up these defaults on the next Token Board load.
        </Alert>

        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <TextField
              size="small"
              placeholder="Search restaurant, hospital, kirana…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Payment</InputLabel>
              <Select
                label="Payment"
                value={payFilter}
                onChange={(e) => setPayFilter(e.target.value)}
              >
                <MenuItem value="all">All payment modes</MenuItem>
                <MenuItem value="prepaid">Prepaid only</MenuItem>
                <MenuItem value="postpaid">Postpaid only</MenuItem>
              </Select>
            </FormControl>
            <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
              Refresh
            </Button>
          </Stack>
        </Paper>

        {platform && (
          <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={800}>Platform fallback</Typography>
                <Typography variant="body2" color="text.secondary">
                  Used when a shop has no matching category. Current:{' '}
                  {platform.resolved?.postpaid ? 'postpaid' : 'prepaid'} · pack{' '}
                  {platform.resolved?.packId || 'hotel'}
                </Typography>
              </Box>
              {payChip(!!platform.resolved?.postpaid)}
              <Button size="small" variant="outlined" onClick={() => openEditor('platform', platform)}>
                Edit fallback
              </Button>
            </Stack>
          </Paper>
        )}

        {loading && !data ? (
          <Typography color="text.secondary">Loading categories…</Typography>
        ) : (
          filteredGroups.map((group) => {
            const summary = groupPaySummary(group);
            return (
            <Accordion key={group.main_category_id} defaultExpanded={!!search} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                  sx={{ width: '100%', pr: 1 }}
                >
                  <Typography fontWeight={800} sx={{ flex: 1 }}>
                    {group.label}
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' '}
                      · {group.categories.length} categories
                    </Typography>
                  </Typography>
                  {payChip(summary.postpaid, { mixed: summary.mixed })}
                  <Chip size="small" label={summary.pack} />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                  <Button size="small" variant="outlined" onClick={() => openEditor('main', group, group)}>
                    Edit group default
                  </Button>
                  <Button
                    size="small"
                    disabled={saving}
                    onClick={() => applyGroupPay(group, false)}
                  >
                    Group → prepaid
                  </Button>
                  <Button
                    size="small"
                    disabled={saving}
                    onClick={() => applyGroupPay(group, true)}
                  >
                    Group → postpaid
                  </Button>
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell>Key</TableCell>
                        <TableCell>Payment</TableCell>
                        <TableCell>Pack</TableCell>
                        <TableCell>Table / chair</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell align="right"> </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.categories.map((c) => (
                        <TableRow key={c.category_key} hover>
                          <TableCell>
                            <Typography fontWeight={600}>{c.label}</Typography>
                            {!c.is_active && (
                              <Chip size="small" label="Inactive" sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {c.category_key}
                            </Typography>
                          </TableCell>
                          <TableCell>{payChip(!!c.resolved?.postpaid)}</TableCell>
                          <TableCell>{c.resolved?.packId}</TableCell>
                          <TableCell>
                            {c.resolved?.serveAtTable ? 'Yes' : 'No'}
                            {c.resolved?.showTables ? ' · tables' : ''}
                          </TableCell>
                          <TableCell>{sourceChip(c.source)}</TableCell>
                          <TableCell align="right">
                            <Button size="small" onClick={() => openEditor('sub', c, group)}>
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!group.categories.length && (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Typography variant="body2" color="text.secondary">
                              No categories in this group yet. Add them in Shops &amp; Services Index.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
            );
          })
        )}
      </Container>

      <Dialog open={!!editor} onClose={() => !saving && setEditor(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editor?.label}
          <Typography variant="body2" color="text.secondary">
            {editor?.groupLabel
              ? `${editor.groupLabel} · ${editor.category_key}`
              : editor?.category_key}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {editor && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment mode</InputLabel>
                <Select
                  label="Payment mode"
                  value={editor.settings.postpaid ? 'post' : 'pre'}
                  onChange={(e) => setField('postpaid', e.target.value === 'post')}
                >
                  {PAY_OPTIONS.map((o) => (
                    <MenuItem key={String(o.value)} value={o.value ? 'post' : 'pre'}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Label pack</InputLabel>
                <Select
                  label="Label pack"
                  value={editor.settings.packId || 'hotel'}
                  onChange={(e) => setField('packId', e.target.value)}
                >
                  {packs.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Theme</InputLabel>
                <Select
                  label="Theme"
                  value={editor.settings.theme || 'desk'}
                  onChange={(e) => setField('theme', e.target.value)}
                >
                  {(data?.themes || ['desk', 'light', 'dark', 'tv']).map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editor.settings.serveAtTable}
                    onChange={(e) => setField('serveAtTable', e.target.checked)}
                  />
                }
                label="Serve at table / chair"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editor.settings.showTables}
                    onChange={(e) => setField('showTables', e.target.checked)}
                  />
                }
                label="Show tables / chairs section"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editor.settings.splitBoard}
                    onChange={(e) => setField('splitBoard', e.target.checked)}
                  />
                }
                label="Split board by cart item (hospital)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editor.settings.hideNumbers}
                    onChange={(e) => setField('hideNumbers', e.target.checked)}
                  />
                }
                label="Hide token numbers by default"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editor.settings.askAfterBill}
                    onChange={(e) => setField('askAfterBill', e.target.checked)}
                  />
                }
                label="Ask after bill"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editor.settings.showItemNames !== false}
                    onChange={(e) => setField('showItemNames', e.target.checked)}
                  />
                }
                label="Show item names on board"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editor.settings.hideSameCategoryAds !== false}
                    onChange={(e) => setField('hideSameCategoryAds', e.target.checked)}
                  />
                }
                label="Hide same-category ads on wait screen"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editor.settings.showLoksyDigitalPamphlets !== false}
                    onChange={(e) =>
                      setField('showLoksyDigitalPamphlets', e.target.checked)
                    }
                  />
                }
                label="Show Loksy digital pamphlets"
              />
              <TextField
                size="small"
                type="number"
                label="Repeat OP free days (hospital)"
                value={editor.settings.repeatOpFreeDays ?? 0}
                onChange={(e) => setField('repeatOpFreeDays', Number(e.target.value) || 0)}
                inputProps={{ min: 0, max: 365 }}
                helperText="0 = off. Same doctor + phone within N days skips Pay Q."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditor(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveEditor} disabled={saving}>
            {saving ? 'Saving…' : 'Save defaults'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.severity}>{snack.message}</Alert> : null}
      </Snackbar>
    </DashboardLayout>
  );
}
